import { useEffect, useRef } from 'react'
import { Button, Spin } from 'antd'
import { CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons'
import { useComplianceLogs } from '@/features/compliance/hooks'
import { ComplianceJobStatus } from '@/features/compliance/types'

const POLL_INTERVAL_MS = 2000

type AiAnalysisStepProps = {
  clientId: string
  jobId: string
  onNext: () => void
}

export function AiAnalysisStep({ clientId, jobId, onNext }: AiAnalysisStepProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const logsQuery = useComplianceLogs(clientId, jobId, {
    refetchInterval: (query) => {
      const s = query.state.data?.status
      return s === ComplianceJobStatus.Failed ||
        s === ComplianceJobStatus.Parsed ||
        s === ComplianceJobStatus.NeedsReview ||
        s === ComplianceJobStatus.AllMatched
        ? false
        : POLL_INTERVAL_MS
    },
  })

  const status = logsQuery.data?.status
  const logs = logsQuery.data?.logs ?? []
  const isReadyForReview =
    status === ComplianceJobStatus.Parsed ||
    status === ComplianceJobStatus.NeedsReview ||
    status === ComplianceJobStatus.AllMatched
  const isProcessing =
    status === ComplianceJobStatus.Pending ||
    status === ComplianceJobStatus.Processing ||
    status === ComplianceJobStatus.Reviewing

  useEffect(() => {
    const el = scrollContainerRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [logs.length])

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-4 shrink-0 text-center">
        <h2 className="text-xl font-semibold text-slate-900">Analyzing Documents</h2>
        <p className="mt-1 text-slate-600">
          AI is reading your trial balance and matching each line item to workpapers.
        </p>
      </div>

      {isProcessing && (
        <div className="mb-4 flex shrink-0 items-center justify-center">
          <Spin size="large" indicator={<LoadingOutlined style={{ fontSize: 36 }} spin />} />
        </div>
      )}

      <div className="shrink-0 rounded-xl border border-slate-200 bg-white">
        <div
          ref={scrollContainerRef}
          className="h-64 overflow-y-auto overflow-x-hidden p-4"
        >
          <ul className="space-y-2">
            {logs.map((log) => (
              <li key={log.complianceLogId} className="flex items-start gap-2 text-sm">
                <span className="mt-0.5 shrink-0 text-green-600">
                  <CheckCircleOutlined />
                </span>
                <span className="text-slate-700">{log.message}</span>
              </li>
            ))}
            {isProcessing && logs.length === 0 && (
              <li className="flex items-center gap-2 text-slate-500">
                <LoadingOutlined spin />
                <span>Starting analysis...</span>
              </li>
            )}
          </ul>
        </div>
      </div>

      <div className="mt-4 flex shrink-0 justify-end">
        <Button type="primary" size="large" onClick={onNext} disabled={!isReadyForReview}>
          Next: Review Results
        </Button>
      </div>
    </div>
  )
}
